import { useMemo, useRef } from 'react';
import { GRID_LIMIT } from 'flow/utils/meeting-constants';
import {
    selectPinnedFeedNames,
    selectSpeakingMembers,
    selectVideoMembers,
} from 'flow/selectors/video-selectors';
import {
    getMembersInitialPositions,
    getNumberOfScreenShares,
    separateMemberTypes,
    sortMemberTypes,
} from 'flow/utils/meeting-video-grid-utils';
import {
    createStickyOrderState,
    reorderSticky,
} from 'flow/components/meeting/main-area/meeting-tile-utils';
import { useAppSelector } from './useRedux';

export const useMemberManagement = (meetingMembers: any, ownName: string) => {
    const speakingMembers = useAppSelector(selectSpeakingMembers);
    const storeVideoMembers = useAppSelector(selectVideoMembers);
    const pinnedFeedNames = useAppSelector(selectPinnedFeedNames);
    const positionsInGrid = useRef<Array<{ name: string; position: number }>>(
        [],
    );
    // Persistent sticky-order state for the render cap (see reorderSticky).
    const stickyRenderOrder = useRef(createStickyOrderState());

    // Memoized on its real inputs so the sort/position work (and the new result
    // arrays) only recompute when membership, member state, speaking activity or
    // the own name actually change — not on every unrelated re-render of the
    // grid (window resize, chat toggle, hover, etc.).
    const membersToRender = useMemo((): {
        screenSharesToRender: any[];
        nonScreenSharesToRender: any[];
        totalFeedMemberCount: number;
    } => {
        const members = meetingMembers.valueSeq();
        const videoMembers = storeVideoMembers.valueSeq();

        if (!members) {
            return {
                screenSharesToRender: [],
                nonScreenSharesToRender: [],
                totalFeedMemberCount: 0,
            };
        }

        const numberOfScreenShares = getNumberOfScreenShares(videoMembers);
        const numberOfSlotsInGridScreenShareExcluded =
            GRID_LIMIT - numberOfScreenShares;

        positionsInGrid.current = getMembersInitialPositions(
            members,
            positionsInGrid.current,
        );

        const typesLists = separateMemberTypes(
            members,
            videoMembers,
            ownName,
            positionsInGrid.current,
        );
        const { screenShareMemberList, videoMemberList, callerMemberList } =
            typesLists;

        sortMemberTypes(
            videoMemberList,
            screenShareMemberList,
            callerMemberList,
        );

        const screenSharesToRender: any[] = [];
        let j = 0;
        while (j < screenShareMemberList.length && j < GRID_LIMIT) {
            screenSharesToRender.push(screenShareMemberList[j]);
            j++;
        }

        // Stable feed order: camera-on (video) members first, then dial-in
        // callers (each tier already join-ordered by sortMemberTypes). Keep
        // active speakers within the render cap — an off-cap speaker is pulled in
        // and stays (sticky) until a newer speaker displaces the longest-idle
        // occupant — without reshuffling feeds already inside the cap. The
        // visible-window equivalent runs later in MeetingMainArea against the
        // true on-screen count.
        let nonScreenSharesToRender = reorderSticky(
            stickyRenderOrder.current,
            videoMemberList.concat(callerMemberList),
            numberOfSlotsInGridScreenShareExcluded,
            (item: any) => item.member?.get('key'),
            (item: any) => speakingMembers.has(item.member?.get('displayName')),
        ).slice(0, numberOfSlotsInGridScreenShareExcluded);

        // A pinned participant must always render — it forms the stage — even
        // when the GRID_LIMIT cap above dropped them (the cap is order-based and
        // pin-unaware). Add back any pinned member the cap excluded so a pin is
        // honoured regardless of participant count.
        if (!pinnedFeedNames.isEmpty()) {
            const renderedKeys = new Set(
                nonScreenSharesToRender.map((m: any) => m.member?.get('key')),
            );
            const droppedPinned = videoMemberList
                .concat(callerMemberList)
                .filter(
                    (m: any) =>
                        pinnedFeedNames.has(m.member?.get('displayName')) &&
                        !renderedKeys.has(m.member?.get('key')),
                );
            if (droppedPinned.length > 0) {
                nonScreenSharesToRender = nonScreenSharesToRender
                    .concat(droppedPinned)
                    .sort((a: any, b: any) => a.position - b.position);
            }
        }
        // Uncapped count of feed-eligible members (video + dial-in callers,
        // excluding screen shares and self). The render lists above are capped
        // at GRID_LIMIT; this is the true total so the "+N" overflow indicator
        // can account for everyone not shown — not just those the engine hides.
        const totalFeedMemberCount =
            videoMemberList.length + callerMemberList.length;
        return {
            screenSharesToRender,
            nonScreenSharesToRender,
            totalFeedMemberCount,
        };
    }, [
        meetingMembers,
        storeVideoMembers,
        speakingMembers,
        ownName,
        pinnedFeedNames,
    ]);

    return {
        ...membersToRender,
        storeVideoMembers,
    };
};
